/**
 * RecentCountsCard — sidebar card on `/petty-cash` listing the latest
 * physical-count voucher rows for the drawer.
 *
 * Mirrors `prototype/cash/petty-cash.html`'s recent-counts pattern (the counts
 * detail page at `prototype/cash/petty-cash-counts.html` is the full ledger;
 * this is the compact 6-row preview on the drawer detail). The route
 * passes already-formatted rows from `pettyCash.anchorData.recentCounts`.
 *
 * Header "View all →" link points at `/cash/petty-cash/counts` (S33; the
 * route lands in Phase 1c).
 *
 * @see prototype/cash/petty-cash.html — recent counts pattern
 * @see prototype/cash/petty-cash-counts.html — full ledger
 */
import { Link } from "@tanstack/react-router";
import { Card, CardBody, cn } from "@flint/ui";
import { formatDate } from "../../../lib/format.ts";
import { formatVariance, OUTCOME_CHIP_CLASS } from "../lib/format.ts";

// Phase 1c will register `/cash/petty-cash/counts`; cast widens to string so
// the typed Link accepts it now.
const ROUTE_COUNTS = "/cash/petty-cash/counts" as string;

export interface RecentCountRow {
  id: string;
  /** ISO date — `value_date` of the cash-count row, or audit `at` date. */
  countedAt: string;
  /** Signed paise (book − counted). `"0"` means exact. */
  variancePaise: string;
  outcome: "exact" | "short" | "over";
  postedBy: string | null;
  /**
   * `'movement'` — variance-bearing row from `cash_movements`.
   * `'audit'`    — zero-variance count from `audit_entries.verb='counted'`.
   * Optional for backwards compatibility; the card subtly mutes audit rows.
   */
  source?: "movement" | "audit";
}

export interface RecentCountsCardProps {
  /** Up to 6 rows; the caller truncates to match the contract. */
  counts: readonly RecentCountRow[];
}

export function RecentCountsCard({ counts }: RecentCountsCardProps) {
  return (
    <Card>
      <div className="flex items-center justify-between px-4 pt-3.5">
        <h3 className="text-xs font-semibold text-muted uppercase tracking-wider">Recent counts</h3>
        <Link
          to={ROUTE_COUNTS}
          className="text-xs text-muted font-medium hover:text-ink-2 transition-colors"
        >
          View all →
        </Link>
      </div>
      <CardBody className="pt-3">
        {counts.length === 0 ? (
          <p className="text-xs text-muted">
            No counts yet. Run the first physical count to seed the audit trail.
          </p>
        ) : (
          <ul className="flex flex-col gap-0 -my-1">
            {counts.map((row) => {
              const variance = formatVariance(row.variancePaise);
              return (
                <li
                  key={row.id}
                  className="flex items-center justify-between gap-2 py-2 border-b border-dashed border-line last:border-b-0"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-ink-2 font-medium">{formatDate(row.countedAt)}</p>
                    {row.postedBy ? (
                      <p className="text-xs text-muted truncate">{row.postedBy}</p>
                    ) : null}
                  </div>
                  <span
                    className={cn(
                      "inline-flex items-center h-[20px] px-2 text-xs font-semibold rounded-full border whitespace-nowrap capitalize",
                      OUTCOME_CHIP_CLASS[variance.outcome],
                    )}
                  >
                    {variance.text}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}
